import { DestroyRef, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MsalBroadcastService, MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  AuthError,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest
} from '@azure/msal-browser';

import { MatDialog } from '@angular/material/dialog';

import { RegisterInvitedUserService } from '../../data/util/register-invited-user.service';
import { DatabaseCacheService } from '../../data/cache/database-cache.service';
import { StoreAuthService } from './store-auth.service';
import { ConfirmDialogComponent } from '../../shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { b2cPolicies, apiConfig } from '../../../environments/b2c-config';
import { getRedirectUri } from '../../utils';
import { JOIN_SECTION_I18N } from '../../pages/join-section/join-section.i18n';

export type UiAction = 'idle' | 'pre-login' | 'post-login' | 'login' | 'logout' | 'navigate';

interface LoginUiState {
  disableLoginButton: boolean;
  isCallingApi: boolean;
  action: UiAction;
}

interface CallbackState {
  callback?: string;
  data?: {
    key?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthFlowService {
  private readonly loginUiStateSubject = new BehaviorSubject<LoginUiState>(this.restoreUiState());
  private readonly handlePageShow = (): void => {
    this.recoverFromAbortedRedirectIfNeeded();
  };

  readonly loginUiState$: Observable<LoginUiState> = this.loginUiStateSubject.asObservable();

  private initialized = false;
  private postLoginNavigationInProgress = false;
  private hasCompletedPostLoginNavigation = false;
  private readonly postLoginRedirectKey = 'gokuri.postLoginRedirectUrl';
  private readonly forceReloginKey = 'gokuri.forcePromptLogin';
  private readonly pendingNavigationKey = 'gokuri.pendingNavigation';
  private readonly pendingModeKey = 'gokuri.pendingNavigationMode';
  private pendingNavigation = false;
  private pendingMode: UiAction = null;
  private initPromise: Promise<void> | null = null;
  private postLoginRecoveryRetryCount = 0;
  private readonly maxPostLoginRecoveryRetries = 2;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private storeAuth: StoreAuthService,
    private common: DatabaseCacheService,
    private registerInvitedUserService: RegisterInvitedUserService,
    private dialog: MatDialog,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.pendingNavigation = sessionStorage.getItem(this.pendingNavigationKey) === 'true';
    const storedMode = sessionStorage.getItem(this.pendingModeKey);
    this.pendingMode = storedMode ? (storedMode as UiAction) : null;
    if (!this.pendingNavigation && this.isLogoutInProgress()) {
      this.resetUiState();
    }
    this.storeAuth.getIsLoggedInOservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoggedIn) => {
        if (!isLoggedIn) {
          this.hasCompletedPostLoginNavigation = false;
        }
      });

    if (typeof window !== 'undefined') {
      window.addEventListener('pageshow', this.handlePageShow);
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('pageshow', this.handlePageShow);
      });
    }
  }

  init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = this.initializeInternal();
    return this.initPromise;
  }

  private async initializeInternal(): Promise<void> {
    await firstValueFrom(this.authService.initialize());
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.captureIntendedRouteIfNeeded();
    this.handleRedirect();
    this.subscribeToStatusEvents();
    this.subscribeToMsalEvents();
    this.evaluateExistingSession();
  }

  handlePrimaryAction(forceNavigation = false): void {
    if (this.loginUiStateSubject.getValue().isCallingApi) {
      return;
    }

    this.checkAndSetActiveAccount();
    const activeAccount = this.authService.instance.getActiveAccount();
    const hasUsableAccount = !!activeAccount?.idTokenClaims?.['sub'];
    const isLoggedIn = this.storeAuth.getIsLoggedIn();

    // Recover from stale in-memory auth state (e.g. account removed in another tab)
    if (isLoggedIn && !hasUsableAccount) {
      this.storeAuth.setIsLoggedIn(false);
      this.storeAuth.setLoginUserName('');
      this.common.clearAuthCache();
      this.cancelPendingNavigation();
      this.login();
      return;
    }

    if (isLoggedIn) {
      if (forceNavigation && this.hasCompletedPostLoginNavigation) {
        this.hasCompletedPostLoginNavigation = false;
      }
      const mode: UiAction = forceNavigation ? 'login' : 'navigate';
      this.beginNavigation(mode);
      this.handlePostLoginNavigation(undefined, forceNavigation, mode);
    } else {
      this.login();
    }
  }

  login(extraRequest?: Partial<RedirectRequest>, uiAction: UiAction = null): void {
    this.beginNavigation(uiAction ?? 'pre-login');

    const redirectUri = getRedirectUri();
    const baseRequest: RedirectRequest = this.msalGuardConfig.authRequest
      ? { ...(this.msalGuardConfig.authRequest as RedirectRequest) }
      : { scopes: [...apiConfig.scopes] };
    const request = { ...baseRequest, ...extraRequest, redirectUri } as RedirectRequest;
    this.authService.loginRedirect(request);
  }

  loginWithDifferentAccount(): void {
    this.beginNavigation('logout');
    this.storeAuth.setIsLoggedIn(false);
    this.storeAuth.setLoginUserName('');
    this.common.clearAuthCache();
    this.markForceRelogin();
    this.authService.logoutRedirect({ postLogoutRedirectUri: getRedirectUri() })
      .subscribe({
        error: (error) => console.error('[ERROR] AuthFlowService > logoutRedirect', error)
      });
  }

  private evaluateExistingSession(): void {
    const accounts = this.authService.instance.getAllAccounts();
    const usableAccount = accounts.find(account => !!account.idTokenClaims?.['sub']);
    this.storeAuth.setIsLoggedIn(!!usableAccount);

    const logoutInProgress = this.isLogoutInProgress();

    if (usableAccount) {
      this.authService.instance.setActiveAccount(usableAccount);
      // MsalGuard can start a login redirect without going through login(), so
      // pendingNavigation is not always set for a cold deep link. The intended
      // route itself is sufficient evidence that post-login navigation must be
      // resumed once MSAL restores the account.
      if (this.pendingNavigation || this.hasPostLoginRedirect()) {
        this.handlePostLoginNavigation(usableAccount, true, 'login');
      } else if (!logoutInProgress && !this.postLoginNavigationInProgress) {
        // subscribeToStatusEvents may have already kicked off auto-complete
        // (BehaviorSubject replays InteractionStatus.None synchronously);
        // resetting to idle here would flash the "log in as X" button at the
        // user before navigation lands.
        this.resetUiState();
      }
    } else {
      const forceRelogin = this.shouldForceRelogin();
      if (forceRelogin) {
        this.clearForceReloginFlag();
        this.login({ prompt: 'login' }, 'logout');
      } else if (!this.pendingNavigation && !logoutInProgress) {
        this.resetUiState();
      }
    }
  }

  private subscribeToStatusEvents(): void {
    this.msalBroadcastService.inProgress$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status: InteractionStatus) => {
        if (status === InteractionStatus.None) {
          const currentAction = this.loginUiStateSubject.getValue().action;
          if (currentAction === 'logout' && this.pendingNavigation && !this.shouldForceRelogin()) {
            this.cancelPendingNavigation();
            return;
          }
          this.checkAndSetActiveAccount();
          // Direct visits with a cached session intentionally do NOT auto-navigate.
          // The login screen must remain accessible so the user can pick between
          // continuing as the cached account ("X としてログイン") or switching via
          // "別のアカウントでログイン". A fresh login redirect (pendingNavigation=true)
          // is still handled by handleRedirect's next callback.
          if (!this.postLoginNavigationInProgress && !this.pendingNavigation) {
            this.patchUiState({ disableLoginButton: false, isCallingApi: false, action: null });
          }
        } else {
          this.patchUiState({ disableLoginButton: true, isCallingApi: true, action: this.loginUiStateSubject.getValue().action });
        }
      });
  }

  private subscribeToMsalEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((msg: EventMessage) => {
        switch (msg.eventType) {
          case EventType.LOGIN_START:
            this.patchUiState({ disableLoginButton: true });
            break;
          case EventType.LOGIN_SUCCESS:
            this.onMsalSuccess(msg.payload as AuthenticationResult | undefined, msg.eventType);
            break;
          case EventType.ACQUIRE_TOKEN_SUCCESS:
            this.onMsalSuccess(msg.payload as AuthenticationResult | undefined, msg.eventType);
            break;
          case EventType.LOGIN_FAILURE:
            this.onMsalFailure(msg.error as AuthError | undefined);
            break;
          case EventType.LOGOUT_SUCCESS:
            this.onMsalLogoutSuccess();
            break;
        }
      });
  }

  private handleRedirect(): void {
    this.authService.handleRedirectObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: AuthenticationResult | null) => {
          if (!result) {
            this.recoverFromAbortedRedirectIfNeeded();
            return;
          }

          this.storeAuth.setIsLoggedIn(true);
          if (result.account) {
            this.authService.instance.setActiveAccount(result.account);
          }
          if (this.isCallbackFlow(result.state)) {
            this.hasCompletedPostLoginNavigation = true;
            this.setPendingNavigation(false);
            return;
          }
          const shouldForce = this.pendingNavigation;
          if (!shouldForce) {
            return;
          }
          const requestedMode = this.pendingMode || 'login';
          const nextAction: UiAction = requestedMode === 'navigate' ? 'navigate' : 'login';
          if (nextAction === 'login') {
            this.patchUiState({ action: 'post-login', isCallingApi: true });
          } else if (nextAction === 'navigate') {
            this.patchUiState({ action: 'navigate', isCallingApi: true });
          }
          this.pendingMode = nextAction;
          this.handlePostLoginNavigation(result.account, true, nextAction);
        },
        error: (error: AuthError) => {
        }
      });
  }

  private onMsalSuccess(result?: AuthenticationResult, eventType?: EventType): void {
    if (!result) {
      return;
    }

    if (result.account) {
      this.authService.instance.setActiveAccount(result.account);
      this.storeAuth.setIsLoggedIn(true);
    }

    if (this.handleJoinSectionCallback(result)) {
      return;
    }

    if (!this.pendingNavigation) {
      if ((eventType === EventType.LOGIN_SUCCESS || eventType === EventType.ACQUIRE_TOKEN_SUCCESS)
        && this.shouldRecoverPostLoginNavigation(result)) {
        this.handlePostLoginNavigation(result.account, true, 'login');
        return;
      }
      this.patchUiState({ disableLoginButton: false, isCallingApi: false, action: null });
      return;
    }

    if (this.pendingNavigation && this.shouldHandlePostLogin(result.state)) {
      const requestedMode = this.pendingMode || 'login';
      const nextAction: UiAction = requestedMode === 'navigate' ? 'navigate' : 'login';
      if (nextAction === 'login') {
        this.patchUiState({ action: 'post-login', isCallingApi: true });
      } else if (nextAction === 'navigate') {
        this.patchUiState({ action: 'navigate', isCallingApi: true });
      }
      this.pendingMode = nextAction;
      this.handlePostLoginNavigation(result.account, true, nextAction);
    }
  }

  private onMsalLogoutSuccess(): void {
    this.storeAuth.setIsLoggedIn(false);
    this.storeAuth.setLoginUserName('');
    this.common.clearAuthCache();
    if (this.shouldForceRelogin()) {
      this.clearForceReloginFlag();
      this.login({ prompt: 'login' }, 'logout');
    } else {
      this.cancelPendingNavigation();
    }
  }

  private onMsalFailure(error?: AuthError): void {
    if (!error) {
      return;
    }

    this.cancelPendingNavigation();

    if (error.message.indexOf('AADB2C90118') > -1) {
      this.authService.acquireTokenRedirect({
        authority: b2cPolicies.authorities.resetPassword.authority,
        redirectUri: getRedirectUri(),
      } as RedirectRequest);
    }
  }

  private handleJoinSectionCallback(result: AuthenticationResult): boolean {
    const parsedState = this.tryParseState(result.state);
    if (!parsedState || parsedState.callback !== 'join-section') {
      return false;
    }

    // key is already encoded by JoinSectionComponent before being stored in MSAL state
    const key = parsedState.data?.key ?? null;
    const account = result.account;
    const loginUserId = account?.idTokenClaims?.['sub'] as string | undefined;

    if (!key || !loginUserId) {
      this.handlePostLoginNavigation(account, false, 'login');
      return true;
    }

    const bodyRequest = {
      userId: loginUserId,
      createUser: false
    };

    this.registerInvitedUserService.registerInvitedUser(key, bodyRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const response: any = res;
          const error = response?.error;

          if (error) {
            if (error.code === 'E0000xx' && error.message?.includes('user already belongs')) {
              const sectionName = error.data;
              this.dialog.open(ConfirmDialogComponent, {
                disableClose: false,
                data: {
                  title: JOIN_SECTION_I18N.dialog.titleWarning,
                  message: JOIN_SECTION_I18N.message.alreadyBelongs(sectionName),
                  hideNoButton: true,
                  confirmButtonText: JOIN_SECTION_I18N.button.ok,
                }
              });
            } else {
              console.error('[ERROR] AuthFlowService > registerInvitedUser business error', error);
            }
            this.handlePostLoginNavigation(account, false, 'login');
            return;
          }

          let message = '';
          if (response.belongs?.sectionInfo?.sectionName) {
            message = JOIN_SECTION_I18N.message.sectionAccess(response.belongs.sectionInfo.sectionName);
          } else if (response.type === 'sectionGroupAdmin') {
            message = JOIN_SECTION_I18N.message.sectionGroupAdminAccess;
          } else if (response.type === 'systemAdmin') {
            message = JOIN_SECTION_I18N.message.systemAdminAccess;
          }

          this.dialog.open(ConfirmDialogComponent, {
            disableClose: true,
            data: {
              title: JOIN_SECTION_I18N.dialog.titleJoinComplete,
              message,
              hideNoButton: true,
              confirmButtonText: JOIN_SECTION_I18N.button.ok,
            }
          }).afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.handlePostLoginNavigation(account, false, 'login');
            });
        },
        error: (err) => {
          console.error('[ERROR] AuthFlowService > registerInvitedUser', err);
          this.handlePostLoginNavigation(account, false, 'login');
        }
      });

    return true;
  }

  private handlePostLoginNavigation(account?: AccountInfo | null, forceNavigation = false, action: UiAction = 'login'): void {
    if (this.postLoginNavigationInProgress) {
      return;
    }

    if (!forceNavigation && (this.postLoginNavigationInProgress || this.hasCompletedPostLoginNavigation)) {
      return;
    }

    let activeAccount = this.authService.instance.getActiveAccount();
    if ((!activeAccount || !activeAccount.idTokenClaims?.['sub']) && account) {
      this.authService.instance.setActiveAccount(account);
      activeAccount = account;
    }

    if (!activeAccount || !activeAccount.idTokenClaims?.['sub']) {
      if (this.pendingNavigation) {
        this.cancelPendingNavigation();
      }
      return;
    }

    this.postLoginNavigationInProgress = true;
    let navigationResult: boolean | null = null;
    let hasNavigationTarget = false;
    let displayAction: UiAction = action;
    if (action === 'login') {
      displayAction = 'post-login';
    }
    if (action === 'navigate' && this.loginUiStateSubject.getValue().action !== 'navigate') {
      displayAction = 'navigate';
    }
    const shouldShowSpinner = displayAction !== 'idle' && displayAction !== 'pre-login';
    this.patchUiState({ isCallingApi: shouldShowSpinner, disableLoginButton: true, action: displayAction });

    this.common.setLoginUserAndSelectedSection()
      .then(async (responseData) => {
        const storedUrl = this.consumePostLoginRedirect();
        const shouldNavigate = !!storedUrl || this.isOnLoginRoute();
        hasNavigationTarget = this.hasPostLoginNavigationTarget(responseData, storedUrl);

        if (storedUrl) {
          return this.navigateToStoredUrl(storedUrl, responseData);
        }

        if (shouldNavigate) {
          return this.common.navigateAfterLogin(responseData);
        }

        return true;
      })
      .then((result) => {
        navigationResult = result !== false;
        this.hasCompletedPostLoginNavigation = navigationResult;
        if (navigationResult) {
          this.postLoginRecoveryRetryCount = 0;
        }
      })
      .catch((error) => {
        console.error('[ERROR] AuthFlowService > handlePostLoginNavigation()', error);
      })
      .finally(() => {
        this.postLoginNavigationInProgress = false;
        this.setPendingNavigation(false);
        const activeAccount = this.authService.instance.getActiveAccount();
        if (
          navigationResult === false
          && hasNavigationTarget
          && this.shouldAutoCompleteLoginOnLanding(activeAccount)
          && this.postLoginRecoveryRetryCount < this.maxPostLoginRecoveryRetries
        ) {
          this.postLoginRecoveryRetryCount += 1;
          setTimeout(() => {
            this.handlePostLoginNavigation(undefined, true, 'login');
          }, 50);
        }
      });
  }

  private checkAndSetActiveAccount(): void {
    let activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount) {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.authService.instance.setActiveAccount(accounts[0]);
      }
    }
  }

  private captureIntendedRouteIfNeeded(): void {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      return;
    }

    const path = this.getCurrentPath();
    if (!path || this.isAuthRoutePath(path)) {
      return;
    }

    sessionStorage.setItem(this.postLoginRedirectKey, path);
  }

  private consumePostLoginRedirect(): string | null {
    const stored = sessionStorage.getItem(this.postLoginRedirectKey);
    if (stored) {
      sessionStorage.removeItem(this.postLoginRedirectKey);
      if (this.isAuthRoutePath(stored)) {
        return null;
      }
      return stored;
    }
    return null;
  }

  private hasPostLoginRedirect(): boolean {
    const stored = sessionStorage.getItem(this.postLoginRedirectKey);
    return !!stored && !this.isAuthRoutePath(stored);
  }

  private navigateToStoredUrl(storedUrl: string, responseData: any): Promise<boolean> {
    let normalized = storedUrl.trim();
    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }

    if (normalized.startsWith('/home/')) {
      const segments = normalized.split('/');
      const sectionId = segments[2];
      if (sectionId) {
        const sectionPromise = this.setSectionFromResponse(sectionId, responseData);
        if (sectionPromise) {
          return sectionPromise.then(() => this.router.navigateByUrl(normalized));
        }
        return this.common.navigateAfterLogin(responseData);
      }
    }

    if (normalized.startsWith('/section-admin/')) {
      const segments = normalized.split('/');
      const sectionId = segments[2];
      if (sectionId) {
        const sectionPromise = this.setSectionFromResponse(sectionId, responseData);
        if (sectionPromise) {
          return sectionPromise.then(() => this.router.navigateByUrl(normalized));
        }
        return this.common.navigateAfterLogin(responseData);
      }
    }

    return this.router.navigateByUrl(normalized);
  }

  private setSectionFromResponse(sectionId: string, responseData: any): Promise<void> | null {
    if (!responseData?.sectionWithRole) {
      return null;
    }

    const selected = responseData.sectionWithRole.find((s: any) => s.sectionId === sectionId);
    if (!selected) {
      return null;
    }

    return this.common.setSelectedSection({ sectionId: selected.sectionId, sectionName: selected.sectionName });
  }

  private isOnLoginRoute(): boolean {
    const path = this.getCurrentPath();
    if (!path || path === '/' || path.startsWith('/login')) {
      return true;
    }

    const normalizedRaw = path.startsWith('/') ? path.substring(1) : path;
    const normalized = decodeURIComponent(normalizedRaw);
    return normalized.startsWith('state=') || normalized.startsWith('code=') || normalized.startsWith('error=');
  }

  private getCurrentPath(): string {
    // Use only the hash fragment. With hash-based routing the pathname
    // contains the locale prefix served by the static host (/ja/, /en/),
    // not an Angular route, so falling back to it would conflate locale
    // hosting with in-app navigation state.
    const hash = window.location.hash || '';
    return hash.startsWith('#') ? hash.substring(1) : hash;
  }

  private isAuthRoutePath(path: string): boolean {
    if (!path) {
      return true;
    }

    const normalizedRaw = path.startsWith('/') ? path.substring(1) : path;
    const normalized = decodeURIComponent(normalizedRaw);

    if (!normalized || normalized === 'login') {
      return true;
    }

    if (normalized.startsWith('state=') || normalized.startsWith('code=') || normalized.startsWith('error=')) {
      return true;
    }

    return normalized === 'state' || normalized === 'code' || normalized === 'error';
  }

  private shouldHandlePostLogin(state?: string | null): boolean {
    const parsedState = this.tryParseState(state);
    if (!parsedState) {
      return true;
    }

    return parsedState.callback !== 'join-section';
  }

  private isCallbackFlow(state?: string | null): boolean {
    const parsedState = this.tryParseState(state);
    return !!parsedState?.callback;
  }

  private tryParseState(state?: string | null): CallbackState | null {
    if (!state) {
      return null;
    }
    try {
      return JSON.parse(state);
    } catch {
      return null;
    }
  }

  private patchUiState(patch: Partial<LoginUiState>): void {
    const current = this.loginUiStateSubject.getValue();
    const next = { ...current, ...patch };
    this.loginUiStateSubject.next(next);
    sessionStorage.setItem('gokuri.loginUiState', JSON.stringify(next));
  }

  private restoreUiState(): LoginUiState {
    const stored = sessionStorage.getItem('gokuri.loginUiState');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // ignore
      }
    }
    return { disableLoginButton: false, isCallingApi: false, action: 'idle' };
  }

  private setPendingNavigation(value: boolean, mode: UiAction = null): void {
    this.pendingNavigation = value;
    if (value) {
      sessionStorage.setItem(this.pendingNavigationKey, 'true');
    } else {
      sessionStorage.removeItem(this.pendingNavigationKey);
    }
    this.pendingMode = mode;
    if (mode) {
      sessionStorage.setItem(this.pendingModeKey, mode);
    } else {
      sessionStorage.removeItem(this.pendingModeKey);
    }
  }

  private resetUiState(force: boolean = false): void {
    if (!force && this.pendingNavigation) {
      return;
    }
    this.patchUiState({ disableLoginButton: false, isCallingApi: false, action: 'idle' });
  }

  private cancelPendingNavigation(): void {
    this.setPendingNavigation(false);
    this.resetUiState(true);
  }

  private isLogoutInProgress(): boolean {
    return this.loginUiStateSubject.getValue().action === 'logout';
  }

  private beginNavigation(mode: UiAction): void {
    this.postLoginRecoveryRetryCount = 0;
    this.setPendingNavigation(true, mode);
    switch (mode) {
      case 'logout':
        this.patchUiState({ disableLoginButton: true, isCallingApi: true, action: 'logout' });
        break;
      case 'navigate':
        this.patchUiState({ disableLoginButton: true, isCallingApi: true, action: 'navigate' });
        break;
      case 'pre-login':
        this.patchUiState({ disableLoginButton: true, isCallingApi: false, action: 'pre-login' });
        break;
      default:
        this.patchUiState({ disableLoginButton: true, isCallingApi: true, action: mode });
        break;
    }
  }

  private markForceRelogin(): void {
    sessionStorage.setItem(this.forceReloginKey, 'true');
  }

  private clearForceReloginFlag(): void {
    sessionStorage.removeItem(this.forceReloginKey);
  }

  private shouldForceRelogin(): boolean {
    return sessionStorage.getItem(this.forceReloginKey) === 'true';
  }

  private shouldRecoverPostLoginNavigation(result: AuthenticationResult): boolean {
    const loginUserId = result.account?.idTokenClaims?.['sub'];
    if (!loginUserId) {
      return false;
    }

    if (!this.isOnLoginRoute()) {
      return false;
    }

    return !this.postLoginNavigationInProgress && !this.hasCompletedPostLoginNavigation;
  }

  private shouldAutoCompleteLoginOnLanding(account?: AccountInfo | null): boolean {
    const loginUserId = account?.idTokenClaims?.['sub'];
    if (!loginUserId) {
      return false;
    }

    if (this.pendingNavigation || this.shouldForceRelogin()) {
      return false;
    }

    if (!this.isOnLoginRoute()) {
      return false;
    }

    if (this.postLoginRecoveryRetryCount >= this.maxPostLoginRecoveryRetries) {
      return false;
    }

    return !this.postLoginNavigationInProgress && !this.hasCompletedPostLoginNavigation;
  }

  private hasPostLoginNavigationTarget(responseData: any, storedUrl: string | null): boolean {
    if (storedUrl) {
      return true;
    }

    if (responseData?.sectionWithRole?.length > 0) {
      return true;
    }

    if (responseData?.systemAdminAssignmentDocument) {
      return true;
    }

    if (responseData?.sectionGroupAdminAssignmentDocuments?.length > 0) {
      return true;
    }

    return false;
  }

  private recoverFromAbortedRedirectIfNeeded(): boolean {
    if (!this.pendingNavigation || this.shouldForceRelogin() || this.hasAuthResponseInUrl()) {
      return false;
    }

    this.checkAndSetActiveAccount();
    const account = this.authService.instance.getActiveAccount();
    if (account?.idTokenClaims?.['sub']) {
      return false;
    }

    this.cancelPendingNavigation();
    return true;
  }

  private hasAuthResponseInUrl(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    if (this.hasMsalResponseParams(window.location.search)) {
      return true;
    }
    if (this.hasMsalResponseParams(window.location.hash)) {
      return true;
    }

    return this.isAuthCallbackPath(this.getCurrentPath() || '');
  }

  private hasMsalResponseParams(raw: string | null | undefined): boolean {
    if (!raw) {
      return false;
    }

    const trimmed = raw.replace(/^[?#]/, '');
    const queryLike = trimmed.includes('?') ? trimmed.substring(trimmed.indexOf('?') + 1) : trimmed;
    const normalized = queryLike.startsWith('/') ? queryLike.substring(1) : queryLike;
    const params = new URLSearchParams(normalized);

    return params.has('code') || params.has('state') || params.has('error');
  }

  private isAuthCallbackPath(path: string): boolean {
    const normalizedRaw = path.startsWith('/') ? path.substring(1) : path;
    const normalized = decodeURIComponent(normalizedRaw);
    return normalized.startsWith('state=') || normalized.startsWith('code=') || normalized.startsWith('error=');
  }

}
